/**
 * VisitTypeSettings (v2)
 * ──────────────────────────────────────────────────────────────────────
 * Settings → Visit Types list + per-type editor screen.
 *
 * Mode behavior:
 *   • Demo mode: state lives locally, mirrors DEMO_VISIT_TYPES_CATALOG.
 *   • Real mode: visit types are fetched / persisted via the scheduler
 *     API (`/api/scheduler/visit-types`). Because the API schema is a
 *     subset of the rich UI shape (name, duration_min, buffer_min,
 *     is_active), the extra UI-only fields (color, flags, sections, …)
 *     are preserved locally for the current session but not round-
 *     tripped to the backend.
 *
 * Per echo_appt_lightbox_and_visit_type_settings_v2.md Part 2.
 */
import { useEffect, useState } from 'react';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { cn } from '../../../../lib/utils';
import {
  Plus, ArrowLeft, Save, Trash2, CheckCircle2, Loader2, AlertTriangle, Info, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../../../contexts/AppModeContext';
import * as schedulerApi from '../../../../lib/api/schedulerApi';
import {
  DEMO_VISIT_TYPES_CATALOG, DEMO_VISIT_TYPE_FLAGS, DEMO_VISIT_TYPE_CATEGORIES,
  DEMO_CLINICAL_SECTIONS,
} from '../../../../lib/fixtures/schedulerV2Demo';
import VisitTypeNoteConfigPanel from '../../../shared/visitTypeNoteConfig/VisitTypeNoteConfigPanel';

const COLOR_MAP = {
  Blue: '#3b82f6', Green: '#22c55e', Red: '#ef4444',
  Amber: '#f59e0b', Purple: '#8b5cf6', Teal: '#14b8a6',
  Gray: '#94a3b8', Pink: '#ec4899', Coral: '#ff7f50',
};
const COLOR_OPTIONS = Object.keys(COLOR_MAP);

const Toggle = ({ on, onToggle, testId }) => (
  <button
    onClick={onToggle}
    className={cn('relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0', on ? 'bg-[#6b5ce7]' : 'bg-slate-200')}
    style={{ border: 'none', cursor: 'pointer' }}
    data-testid={testId}
    role="switch"
    aria-checked={on}
  >
    <span className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform" style={{ transform: on ? 'translateX(18px)' : 'translateX(2px)' }} />
  </button>
);

const Stepper = ({ value, onChange, testId }) => (
  <div className="inline-flex items-center gap-0 border border-slate-200 rounded-md overflow-hidden" data-testid={testId}>
    <button onClick={() => onChange(Math.max(0, value - 1))} className="h-7 w-7 inline-flex items-center justify-center text-slate-600 hover:bg-slate-100" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>−</button>
    <span className="h-7 w-10 inline-flex items-center justify-center text-[12px] font-semibold text-slate-800 border-l border-r border-slate-100">{value}</span>
    <button onClick={() => onChange(value + 1)} className="h-7 w-7 inline-flex items-center justify-center text-slate-600 hover:bg-slate-100" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>+</button>
  </div>
);

const ListView = ({ types, onOpenEdit, onAdd, onDuplicate }) => (
  <div className="space-y-3" data-testid="visit-types-list">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-semibold text-slate-800">Visit Types</p>
        <p className="text-[10px] text-slate-500">Configure visit type name, duration, color, flags, and clinical sections.</p>
      </div>
      <Button size="sm" className="text-xs h-8 gap-1 bg-[#111] hover:bg-[#222]" onClick={onAdd} data-testid="visit-types-add-btn">
        <Plus className="h-3 w-3" /> Add visit type
      </Button>
    </div>
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <table className="w-full text-[11px]">
        <thead className="bg-slate-50 text-[9px] text-slate-500 uppercase tracking-wide">
          <tr>
            <th className="text-left px-3 py-2 font-semibold">Color</th>
            <th className="text-left px-3 py-2 font-semibold">Name</th>
            <th className="text-left px-3 py-2 font-semibold">Abbr.</th>
            <th className="text-left px-3 py-2 font-semibold">Duration</th>
            <th className="text-left px-3 py-2 font-semibold">Status</th>
            <th className="text-left px-3 py-2 font-semibold">Default</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {types.map((vt) => (
            <tr key={vt.id} className="hover:bg-slate-50/60" data-testid={`visit-type-row-${vt.id}`}>
              <td className="px-3 py-2"><span className="inline-block h-3 w-3 rounded-full" style={{ background: COLOR_MAP[vt.color] || '#94a3b8' }} /></td>
              <td className="px-3 py-2 font-medium text-slate-800">{vt.name}</td>
              <td className="px-3 py-2 text-slate-500">{vt.abbreviation}</td>
              <td className="px-3 py-2 text-slate-600">{vt.defaultDuration}m</td>
              <td className="px-3 py-2">
                <Badge className={cn('text-[9px] border', vt.status === 'enabled' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200')}>
                  {vt.status}
                </Badge>
              </td>
              <td className="px-3 py-2 text-slate-600">
                {vt.showAsDefault && <CheckCircle2 className="h-3 w-3 text-[#6b5ce7]" />}
              </td>
              <td className="px-3 py-2 text-right">
                <div className="inline-flex items-center gap-1 justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[10px] h-7 gap-1 text-slate-500 hover:text-slate-800"
                    onClick={() => onDuplicate(vt)}
                    data-testid={`visit-type-duplicate-${vt.id}`}
                    aria-label={`Duplicate ${vt.name}`}
                    title="Duplicate"
                  >
                    <Copy className="h-3 w-3" /> Duplicate
                  </Button>
                  <Button size="sm" variant="outline" className="text-[10px] h-7" onClick={() => onOpenEdit(vt)} data-testid={`visit-type-edit-${vt.id}`}>
                    Edit
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Section = ({ title, desc, children, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={testId}>
    <div>
      <p className="text-[12px] font-semibold text-slate-800">{title}</p>
      {desc && <p className="text-[10px] text-slate-500 mt-0.5">{desc}</p>}
    </div>
    {children}
  </div>
);

const EditView = ({ record, onBack, onSave, onDelete, saving = false }) => {
  const [draft, setDraft] = useState({ ...record });

  const setFlag = (id, on) => setDraft((d) => ({ ...d, flags: { ...d.flags, [id]: on } }));
  const setSection = (id, on) => setDraft((d) => ({ ...d, sections: { ...d.sections, [id]: on } }));
  const uncheckAllSections = () => setDraft((d) => ({ ...d, sections: {} }));

  const groupedFlags = {};
  DEMO_VISIT_TYPE_FLAGS.forEach((f) => {
    if (!groupedFlags[f.group]) groupedFlags[f.group] = [];
    groupedFlags[f.group].push(f);
  });

  const showRecurrence = draft.flags?.wellChild || draft.flags?.wellness;

  return (
    <div className="space-y-4" data-testid="visit-type-edit">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button
            onClick={onBack}
            className="text-[11px] text-slate-500 inline-flex items-center gap-1 hover:text-slate-700"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            data-testid="visit-type-back-btn"
          >
            <ArrowLeft className="h-3 w-3" /> Back to Visit Types
          </button>
          <p className="text-sm font-semibold text-slate-800 mt-1">Edit visit type</p>
          <p className="text-[11px] text-slate-500 font-medium">&quot;{draft.name}&quot;</p>
        </div>
        <div className="flex items-center gap-2">
          {draft.id && (
            <Button variant="ghost" size="sm" className="text-[11px] text-red-600 hover:text-red-700 gap-1" onClick={() => onDelete(draft)} data-testid="visit-type-delete-btn">
              <Trash2 className="h-3 w-3" /> Delete type
            </Button>
          )}
          <Button size="sm" className="text-[11px] h-8 gap-1 bg-[#111] hover:bg-[#222]" onClick={() => onSave(draft)} disabled={saving} data-testid="visit-type-save-btn">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      {/* Basic */}
      <Section title="Basic information" testId="vt-section-basic">
        <div>
          <p className="text-[10px] text-slate-500 mb-1">Visit type name</p>
          <input
            type="text"
            value={draft.name || ''}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] outline-none bg-slate-50"
            data-testid="vt-name"
          />
          <p className="text-[9px] text-slate-400 mt-1">This name appears in the booking lightbox and on the calendar.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] text-slate-500 mb-1">Abbreviation</p>
            <input value={draft.abbreviation || ''} onChange={(e) => setDraft({ ...draft, abbreviation: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="vt-abbr" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 mb-1">Color tag</p>
            <div className="flex items-center gap-1 flex-wrap">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c}
                  onClick={() => setDraft({ ...draft, color: c })}
                  className={cn('h-6 w-6 rounded-full inline-flex items-center justify-center', draft.color === c && 'ring-2 ring-offset-1 ring-slate-800')}
                  style={{ background: COLOR_MAP[c], border: 'none', cursor: 'pointer' }}
                  aria-label={c}
                  data-testid={`vt-color-${c.toLowerCase()}`}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] text-slate-500 mb-1">Status</p>
            <select value={draft.status || 'enabled'} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="vt-status">
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
          <div>
            <p className="text-[10px] text-slate-500 mb-1">Profile</p>
            <select value={draft.profile || 'Normal'} onChange={(e) => setDraft({ ...draft, profile: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="vt-profile">
              <option value="Normal">Normal</option>
              <option value="Extended">Extended</option>
              <option value="Minimal">Minimal</option>
            </select>
          </div>
        </div>
        <div>
          <p className="text-[10px] text-slate-500 mb-1">Default duration</p>
          <div className="flex gap-1.5">
            {[15, 20, 30, 45, 60].map((d) => (
              <button
                key={d}
                onClick={() => setDraft({ ...draft, defaultDuration: d })}
                className={cn('px-3 py-1 rounded-md text-[11px] font-medium border', draft.defaultDuration === d ? 'bg-[#111] text-white border-[#111]' : 'bg-white text-slate-600 border-slate-200')}
                style={{ cursor: 'pointer' }}
                data-testid={`vt-dur-${d}`}
              >
                {d}m
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between py-1">
          <div>
            <p className="text-[11px] font-medium text-slate-800">Show as default type</p>
            <p className="text-[9px] text-slate-400">Appears in the primary tiles row of the booking lightbox.</p>
          </div>
          <Toggle
            on={!!draft.showAsDefault}
            onToggle={() => setDraft((d) => ({ ...d, showAsDefault: !d.showAsDefault, flags: { ...d.flags, defaultType: !d.showAsDefault } }))}
            testId="vt-show-default"
          />
        </div>
      </Section>

      {/* Staff labels */}
      <Section title="Staff labels" desc="These labels appear in the rooming and clinical workflow views." testId="vt-section-labels">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] text-slate-500 mb-1">Physician label</p>
            <input value={draft.physicianLabel || ''} onChange={(e) => setDraft({ ...draft, physicianLabel: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="vt-physician-label" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 mb-1">Medical assistant label</p>
            <input value={draft.maLabel || ''} onChange={(e) => setDraft({ ...draft, maLabel: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="vt-ma-label" />
          </div>
        </div>
      </Section>

      {/* Flags */}
      <Section title="Visit behavior" testId="vt-section-flags">
        {Object.entries(groupedFlags).map(([group, flags]) => (
          <div key={group} className="space-y-2">
            <p className="text-[9px] text-slate-400 uppercase font-semibold tracking-wider">{DEMO_VISIT_TYPE_CATEGORIES[group]}</p>
            <div className="space-y-2">
              {flags.map((f) => {
                const on = !!draft.flags?.[f.id];
                return (
                  <div key={f.id} className="flex items-start justify-between gap-3 py-1" data-testid={`vt-flag-row-${f.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-[11px] font-medium', on ? 'text-slate-800' : 'text-slate-500')}>{f.label}</p>
                      <p className={cn('text-[9px] mt-0.5', on ? 'text-slate-500' : 'text-slate-300')}>{f.desc}</p>
                    </div>
                    <Toggle on={on} onToggle={() => setFlag(f.id, !on)} testId={`vt-flag-${f.id}`} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </Section>

      {/* Recurrence (conditional) */}
      {showRecurrence && (
        <Section title="Visit frequency" desc="How often should this visit type be offered per patient? Echo uses this for care gap detection and outreach." testId="vt-section-recurrence">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-slate-500">Every</span>
              <Stepper value={draft.recurrence?.years || 0} onChange={(v) => setDraft({ ...draft, recurrence: { ...draft.recurrence, years: v } })} testId="vt-recurrence-years" />
              <span className="text-[10px] text-slate-500">years</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Stepper value={draft.recurrence?.months || 0} onChange={(v) => setDraft({ ...draft, recurrence: { ...draft.recurrence, months: v } })} testId="vt-recurrence-months" />
              <span className="text-[10px] text-slate-500">months</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Stepper value={draft.recurrence?.days || 0} onChange={(v) => setDraft({ ...draft, recurrence: { ...draft.recurrence, days: v } })} testId="vt-recurrence-days" />
              <span className="text-[10px] text-slate-500">days</span>
            </div>
          </div>
          <p className="text-[9px] text-slate-400">
            Example: {draft.recurrence?.years || 0} years · {draft.recurrence?.months || 0} months · {draft.recurrence?.days || 0} days
          </p>
        </Section>
      )}

      {/* Clinical sections */}
      <Section title="Clinical sections" desc="These sections will be pre-loaded in the provider's encounter view when this visit type is opened." testId="vt-section-clinical">
        <div className="flex items-center justify-end -mt-2">
          <button
            onClick={uncheckAllSections}
            className="text-[10px] text-slate-500 hover:text-slate-700"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            data-testid="vt-uncheck-all"
          >
            Uncheck all
          </button>
        </div>
        {Object.entries(DEMO_CLINICAL_SECTIONS).map(([group, items]) => (
          <div key={group} className="space-y-2">
            <p className="text-[9px] text-slate-400 uppercase font-semibold tracking-wider">{group}</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {items.map((sec) => {
                const on = !!draft.sections?.[sec.id];
                return (
                  <label key={sec.id} className="flex items-center gap-2 cursor-pointer" title={sec.desc} data-testid={`vt-section-row-${sec.id}`}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => setSection(sec.id, !on)}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-[#111] accent-[#111]"
                      data-testid={`vt-section-${sec.id}`}
                    />
                    <span className="text-[11px] text-slate-700">{sec.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </Section>

      {/* Image settings */}
      <Section title="Document display settings" desc="Controls how attached images appear in encounter documents." testId="vt-section-image">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-[10px] text-slate-500 mb-1">Images per line</p>
            <input type="number" value={draft.image?.perLine || 2} onChange={(e) => setDraft({ ...draft, image: { ...draft.image, perLine: parseInt(e.target.value) || 1 } })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="vt-img-perline" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 mb-1">Image width (px)</p>
            <input type="number" value={draft.image?.width || 200} onChange={(e) => setDraft({ ...draft, image: { ...draft.image, width: parseInt(e.target.value) || 100 } })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="vt-img-width" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 mb-1">Image height (px)</p>
            <input type="number" value={draft.image?.height || 250} onChange={(e) => setDraft({ ...draft, image: { ...draft.image, height: parseInt(e.target.value) || 100 } })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] outline-none bg-slate-50" data-testid="vt-img-height" />
          </div>
        </div>
      </Section>

      {/* Note structure + copy-forward (demo-only). Mirrored in Lumina →
          Preferences so the two surfaces read/write the same per-visit-type
          config. */}
      {draft.id && (
        <VisitTypeNoteConfigPanel visitType={{ id: draft.id, name: draft.name }} surface="scheduler" />
      )}
    </div>
  );
};

/* ── Main ── */
const EMPTY_UI_DRAFT = {
  color: 'Blue', type: 'Standard', status: 'enabled', profile: 'Normal',
  showAsDefault: false, abbreviation: '',
  physicianLabel: 'Physician', maLabel: 'Medical Asst',
  flags: {}, recurrence: { years: 0, months: 0, days: 0 },
  sections: {}, image: { perLine: 2, width: 200, height: 250 },
};

// Map API shape → rich UI shape. UI-only fields are filled with safe defaults.
const apiToUi = (vt) => ({
  ...EMPTY_UI_DRAFT,
  id: vt.id,
  name: vt.name || '',
  defaultDuration: vt.duration_min || 30,
  buffer: vt.buffer_min || 0,
  status: vt.is_active === false ? 'disabled' : 'enabled',
});

const uiToApi = (draft) => ({
  name: (draft.name || '').trim(),
  duration_min: parseInt(draft.defaultDuration, 10) || 30,
  buffer_min: parseInt(draft.buffer || 0, 10) || 0,
  is_active: draft.status !== 'disabled',
});

const VisitTypeSettings = () => {
  const { isDemo } = useAppMode();
  const [types, setTypes] = useState(isDemo ? DEMO_VISIT_TYPES_CATALOG : []);
  const [editing, setEditing] = useState(null); // visit type being edited
  const [creatingNew, setCreatingNew] = useState(false);
  const [loading, setLoading] = useState(!isDemo);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    if (isDemo) return;
    setLoading(true);
    setError(null);
    const res = await schedulerApi.listVisitTypes();
    if (res.ok) {
      setTypes((res.data?.visit_types || res.data?.items || []).map(apiToUi));
    } else {
      setError(res.error || { message: 'Failed to load visit types' });
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo]);

  const handleOpenEdit = (vt) => {
    setEditing(vt);
    setCreatingNew(false);
  };
  const handleAdd = () => {
    setEditing({
      id: null, name: '', abbreviation: '', color: 'Blue', type: 'Standard', status: 'enabled',
      profile: 'Normal', defaultDuration: 30, buffer: 0, showAsDefault: false,
      physicianLabel: 'Physician', maLabel: 'Medical Asst',
      flags: {}, recurrence: { years: 0, months: 0, days: 0 }, sections: {}, image: { perLine: 2, width: 200, height: 250 },
    });
    setCreatingNew(true);
  };

  const handleSave = async (draft) => {
    if (!draft.name?.trim()) {
      toast.error('Visit type name is required');
      return;
    }
    if (isDemo) {
      if (creatingNew) {
        const id = `vt-${Date.now()}`;
        setTypes((prev) => [...prev, { ...draft, id }]);
        toast.success('Visit type created · Changes take effect immediately');
      } else {
        setTypes((prev) => prev.map((t) => (t.id === draft.id ? draft : t)));
        toast.success('Visit type updated · Changes take effect immediately');
      }
      setEditing(null);
      setCreatingNew(false);
      return;
    }
    // Real mode: call API
    setSaving(true);
    const body = uiToApi(draft);
    const res = creatingNew
      ? await schedulerApi.createVisitType(body)
      : await schedulerApi.updateVisitType(draft.id, body);
    setSaving(false);
    if (!res.ok) {
      toast.error(creatingNew ? 'Failed to create visit type' : 'Failed to update visit type', {
        description: res.error?.message,
      });
      return;
    }
    toast.success(creatingNew ? 'Visit type created' : 'Visit type updated');
    setEditing(null);
    setCreatingNew(false);
    refresh();
  };

  const handleDelete = async (draft) => {
    if (isDemo) {
      setTypes((prev) => prev.filter((t) => t.id !== draft.id));
      toast.info(`${draft.name} deleted`);
      setEditing(null);
      return;
    }
    if (!draft.id) {
      setEditing(null);
      setCreatingNew(false);
      return;
    }
    setSaving(true);
    const res = await schedulerApi.deleteVisitType(draft.id);
    setSaving(false);
    if (!res.ok) {
      toast.error('Failed to delete visit type', { description: res.error?.message });
      return;
    }
    toast.info(`${draft.name} deleted`);
    setEditing(null);
    refresh();
  };

  /* ── Duplicate: clone an existing visit type with auto-renamed "X (copy)" ── */
  const handleDuplicate = async (source) => {
    const baseName = (source.name || 'Visit type').replace(/\s*\(copy( \d+)?\)\s*$/i, '');
    // Find a unique "(copy)" suffix so repeated clicks don't collide.
    const existingNames = new Set(types.map((t) => t.name));
    let candidate = `${baseName} (copy)`;
    let n = 2;
    while (existingNames.has(candidate)) {
      candidate = `${baseName} (copy ${n++})`;
    }
    const cloneUi = {
      ...source,
      id: null,
      name: candidate,
      showAsDefault: false, // never clone the default-type flag
    };
    if (isDemo) {
      const id = `vt-${Date.now()}`;
      setTypes((prev) => [...prev, { ...cloneUi, id }]);
      toast.success(`Duplicated as "${candidate}"`);
      return;
    }
    // Real mode — create via API then refresh
    setSaving(true);
    const res = await schedulerApi.createVisitType(uiToApi(cloneUi));
    setSaving(false);
    if (!res.ok) {
      toast.error('Failed to duplicate visit type', { description: res.error?.message });
      return;
    }
    toast.success(`Duplicated as "${candidate}"`);
    refresh();
  };

  if (editing) {
    return (
      <EditView
        record={editing}
        onBack={() => { setEditing(null); setCreatingNew(false); }}
        onSave={handleSave}
        onDelete={handleDelete}
        saving={saving}
      />
    );
  }

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="visit-types-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto mb-2" />
        <p className="text-[11px] text-slate-500">Loading visit types…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 p-4 flex items-center gap-2" data-testid="visit-types-error">
        <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
        <span className="text-[11px] text-red-700 flex-1">{error.message || 'Failed to load visit types'}</span>
        <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={refresh}>Retry</Button>
      </div>
    );
  }

  return (
    <>
      {!isDemo && (
        <div className="p-2.5 rounded-lg bg-blue-50 border border-blue-100 flex items-start gap-2 mb-3" data-testid="visit-types-real-info">
          <Info className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />
          <p className="text-[10px] text-blue-700">
            Name, duration, buffer, and status persist to the backend. Color tag, flags,
            and clinical sections are UI-only for this session.
          </p>
        </div>
      )}
      <ListView types={types} onOpenEdit={handleOpenEdit} onAdd={handleAdd} onDuplicate={handleDuplicate} />
    </>
  );
};

export default VisitTypeSettings;
